// React Query hooks for Xoe memory (S10). Keys: ['xoe','memories',scope,status ?? null] and ['xoe','observations',patientId].
// Every memory mutation invalidates the bare ['xoe','memories'] prefix on SETTLE: a 403/409 means the row is not what the screen showed.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	listXoeMemories,
	createXoeMemory,
	confirmXoeMemory,
	dismissXoeMemory,
	withdrawXoeMemory,
	restoreXoeMemory,
} from "../../api/xoe/XoeMemoriesApi";
import { listXoeObservations, withdrawXoeObservation } from "../../api/healthcare/XoeObservationsApi";
import type {
	CreateXoeMemoryPayload,
	XoeMemory,
	XoeMemoryScope,
	XoeMemoryStatus,
	XoeObservation,
} from "../../types/xoe/XoeMemoryTypes";
import type { UserUuid } from "../../types/identity";

export const XOE_MEMORIES_KEY = ["xoe", "memories"] as const;

export const xoeMemoriesKey = (scope: XoeMemoryScope, status?: XoeMemoryStatus) =>
	[...XOE_MEMORIES_KEY, scope, status ?? null] as const;

/** Omitting `status` lists proposed + confirmed. `enabled: false` defers a list nobody is looking at. */
export const useXoeMemories = (scope: XoeMemoryScope, status?: XoeMemoryStatus, enabled = true) =>
	useQuery<XoeMemory[]>({
		queryKey: xoeMemoriesKey(scope, status),
		queryFn: () => listXoeMemories({ scope, status }),
		enabled,
	});

const useInvalidateMemories = () => {
	const queryClient = useQueryClient();
	return () => queryClient.invalidateQueries({ queryKey: XOE_MEMORIES_KEY });
};

export const useCreateXoeMemory = () => {
	const invalidate = useInvalidateMemories();
	return useMutation({
		mutationFn: (payload: CreateXoeMemoryPayload) => createXoeMemory(payload),
		onSettled: invalidate,
	});
};

export const useConfirmXoeMemory = () => {
	const invalidate = useInvalidateMemories();
	return useMutation({ mutationFn: (id: string) => confirmXoeMemory(id), onSettled: invalidate });
};

export const useDismissXoeMemory = () => {
	const invalidate = useInvalidateMemories();
	return useMutation({ mutationFn: (id: string) => dismissXoeMemory(id), onSettled: invalidate });
};

export const useWithdrawXoeMemory = () => {
	const invalidate = useInvalidateMemories();
	return useMutation({ mutationFn: (id: string) => withdrawXoeMemory(id), onSettled: invalidate });
};

export const useRestoreXoeMemory = () => {
	const invalidate = useInvalidateMemories();
	return useMutation({ mutationFn: (id: string) => restoreXoeMemory(id), onSettled: invalidate });
};

// ── Patient observations (healthcare) ─────────────────────────────────────────

export const xoeObservationsKey = (patientId: UserUuid) => ["xoe", "observations", patientId] as const;

export const useXoeObservations = (patientId: UserUuid | undefined) =>
	useQuery<XoeObservation[]>({
		queryKey: xoeObservationsKey(patientId ?? ""),
		queryFn: () => listXoeObservations(patientId as UserUuid),
		enabled: !!patientId,
	});

export const useWithdrawXoeObservation = (patientId: UserUuid) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (observationId: number) => withdrawXoeObservation(observationId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: xoeObservationsKey(patientId) }),
	});
};
